import type { BLEND_MODES } from "pixi.js";
import type { TweenEasing } from "@slot/presentation";

/**
 * The light that flares at the cannon's mouth when a scatter drops (user, 2026-10-15): the `effects/screenLight` art,
 * drawn in Photoshop's Screen mode (Pixi's `screen` blend) over the grid, under the win labels and the cannon. Position and
 * scale are design units in the composition root, the cannon's own space; durations are seconds on the presentation
 * clock, so turbo, pause and frame-step scale them. Tune by eye.
 */
export interface ScatterLightTuning {
  /** The light's centre: about the cannon's mouth (the barrel's gold rim, over the grid's bottom-left cells). */
  x: number;
  y: number;
  /** At its peak, a multiple of the art's 512 px. */
  scale: number;
  /**
   * The layer's blend, as Photoshop names them: "screen" (Screen), "add" (Linear Dodge (Add)), "multiply" and "normal"
   * draw as they are. The rest ("overlay", "color-dodge", "soft-light", "lighten" …) need Pixi's advanced blend modes
   * imported once (`import "pixi.js/advanced-blend-modes"`) and cost a filter pass while the light shows.
   */
  blendMode: BLEND_MODES;
  /** It grows into `scale` from `scale × fromScale` while it fades in. */
  fromScale: number;
  peakAlpha: number;
  inSeconds: number;
  inEasing: TweenEasing;
  holdSeconds: number;
  outSeconds: number;
  outEasing: TweenEasing;
}

export const SCATTER_LIGHT_TUNING: ScatterLightTuning = {
  x: -400,
  y: 300,
  scale: 5,
  blendMode: "add",
  fromScale: 0.7,
  peakAlpha: 0.3,
  inSeconds: 0.0,
  inEasing: "linear",
  holdSeconds: 0.3,
  outSeconds: 0.8,
  outEasing: "quadOut",
};
